import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  HiOutlineArrowDown,
  HiOutlineArrowUp,
  HiOutlineCash,
  HiOutlineChartBar,
  HiOutlineChartSquareBar,
  HiOutlineDuplicate,
  HiOutlineEye,
  HiOutlineEyeOff,
  HiOutlineGift,
  HiOutlinePaperAirplane,
  HiOutlineStar,
  HiOutlineTrendingUp,
  HiOutlineUserAdd,
  HiOutlineUserGroup,
  HiChevronRight,
  HiOutlineClock,
} from 'react-icons/hi';
import { GiCrown } from 'react-icons/gi';
import { MdOutlineAccountBalanceWallet } from 'react-icons/md';
import { RiCoinLine } from 'react-icons/ri';
import { useWallet } from '../context/WalletContext';
import { useToast } from '../context/ToastContext';
import { api } from '../services/api';
import { DashboardSkeleton } from '../components/Skeleton';
import PancakeSwapGuide from '../components/PancakeSwapGuide';
import './DashboardHome.css';
import { rankTitle } from '../utils/ranks';

function money(value, digits = 2) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return (0).toFixed(digits);
  return amount.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

function padTime(value) {
  return String(value).padStart(2, '0');
}

function remainingParts(resetsAt) {
  const end = new Date(resetsAt).getTime();
  const left = Number.isFinite(end) ? Math.max(0, end - Date.now()) : 0;
  const total = Math.floor(left / 1000);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  return {
    left,
    label: `${padTime(hours)}:${padTime(minutes)}:${padTime(seconds)}`,
  };
}

function sinceParts(from) {
  const start = new Date(from).getTime();
  const elapsed = Number.isFinite(start) ? Math.max(0, Date.now() - start) : 0;
  const total = Math.floor(elapsed / 1000);
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  return {
    days,
    clock: `${padTime(hours)}:${padTime(minutes)}:${padTime(seconds)}`,
    unit: days === 1 ? 'day' : 'days',
  };
}

function TodayDepositBoard({ registeredAt }) {
  const [board, setBoard] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [clock, setClock] = useState(() => remainingParts(null));
  const [since, setSince] = useState(() => sinceParts(registeredAt));

  useEffect(() => {
    let cancelled = false;
    let resetTimer;
    const load = () => {
      api.todayDepositLeaders()
        .then((data) => {
          if (cancelled) return;
          const next = data.board || null;
          setBoard(next);
          setLoaded(true);
          if (resetTimer) window.clearTimeout(resetTimer);
          const wait = next?.resets_at ? new Date(next.resets_at).getTime() - Date.now() : 0;
          if (wait > 0) resetTimer = window.setTimeout(load, wait + 1200);
        })
        .catch(() => {
          if (!cancelled) {
            setBoard(null);
            setLoaded(true);
          }
        });
    };
    load();
    return () => {
      cancelled = true;
      if (resetTimer) window.clearTimeout(resetTimer);
    };
  }, []);

  useEffect(() => {
    const resetsAt = board?.resets_at;
    setClock(remainingParts(resetsAt));
    const id = window.setInterval(() => setClock(remainingParts(resetsAt)), 1000);
    return () => window.clearInterval(id);
  }, [board?.resets_at]);

  useEffect(() => {
    setSince(sinceParts(registeredAt));
    const id = window.setInterval(() => setSince(sinceParts(registeredAt)), 1000);
    return () => window.clearInterval(id);
  }, [registeredAt]);

  const leaders = board?.leaders || [];

  return (
    <section className="home__leaders" aria-label="Daily contest">
      <div className="home__chart-top">
        <div>
          <h2><GiCrown /> Daily Contest</h2>
          <p>Yesterday’s winners</p>
        </div>
        <div className="home__leaders-tools">
          <div className="home__timer">
            <HiOutlineClock aria-hidden="true" />
            <span>
              <em>Next update</em>
              <strong>{clock.label}</strong>
            </span>
          </div>
        </div>
      </div>
      {!loaded ? (
        <p className="home__empty">Loading yesterday’s contest…</p>
      ) : leaders.length ? (
        <div className="home__contest">
          {registeredAt ? (
            <aside className="home__since" aria-label="Days since registration">
              <p>You don't win since</p>
              <strong>{since.days}</strong>
              <span>{since.unit}</span>
              <em>{since.clock}</em>
            </aside>
          ) : null}
          <ol className="home__podium">
            {leaders
              .slice()
              .sort((a, b) => Number(a.place) - Number(b.place))
              .map((row) => (
                <WinnerCard key={row.place} row={row} />
              ))}
          </ol>
        </div>
      ) : (
        <p className="home__empty">Yesterday’s contest is not ready yet.</p>
      )}
    </section>
  );
}

const WINNER_TONES = {
  1: '#e0a106',
  2: '#d7e0ec',
  3: '#d07a3a',
  4: '#5b8def',
  5: '#3d7dff',
};

function WinnerCard({ row }) {
  if (!row) return null;
  const place = Number(row.place) || 0;
  const bonus = Number(row.bonus) || 0;
  return (
    <li
      className={`home__winner home__winner--${place}`}
      style={{ '--place': WINNER_TONES[place] || '#2f7bff' }}
    >
      <div className="home__winner-crown" aria-hidden="true">
        <GiCrown />
      </div>
      <b>{place}</b>
      <div className="home__winner-meta">
        <strong>{row.label || `${place} winner`}</strong>
        {row.wallet ? <span>{row.wallet}</span> : null}
      </div>
      <em>{money(bonus)} <small>USDT</small></em>
      <i className="home__winner-stand" aria-hidden="true" />
    </li>
  );
}

function dayKey(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function buildSeries(events, days) {
  const end = new Date();
  end.setHours(0, 0, 0, 0);
  const buckets = [];
  for (let i = days - 1; i >= 0; i -= 1) {
    const date = new Date(end);
    date.setDate(end.getDate() - i);
    buckets.push({ key: dayKey(date), label: date.toLocaleDateString(undefined, { month: 'short', day: '2-digit' }), value: 0 });
  }
  const index = new Map(buckets.map((bucket, i) => [bucket.key, i]));
  events.forEach((event) => {
    if (!event?.created_at) return;
    const credited = Number(event.amount_credited || 0);
    if (!Number.isFinite(credited) || credited <= 0) return;
    const slot = index.get(dayKey(new Date(event.created_at)));
    if (slot == null) return;
    buckets[slot].value += credited;
  });
  return buckets;
}

const SOURCE_META = {
  self_daily: { label: 'Self daily', color: '#3dd6ff' },
  team_purchase: { label: 'Team purchase', color: '#7aa2ff' },
  rank_bonus: { label: 'Rank bonus', color: '#f6c445' },
  roi_upline: { label: 'ROI upline', color: '#2ee6c7' },
  referral_pool: { label: 'Referral pool', color: '#ff6b9a' },
  claim_charge: { label: 'Claim charge', color: '#c084fc' },
};

function Donut({ slices }) {
  const total = slices.reduce((sum, slice) => sum + slice.value, 0);
  const radius = 46;
  const circumference = 2 * Math.PI * radius;
  let cursor = 0;
  return (
    <div className="home__donut">
      <svg viewBox="0 0 140 140" role="img" aria-label="Portfolio mix">
        <g transform="rotate(-90 70 70)">
          <circle cx="70" cy="70" r={radius} fill="none" stroke="currentColor" strokeOpacity="0.12" strokeWidth="14" />
          {total > 0 && slices.map((slice) => {
            if (slice.value <= 0) return null;
            const length = (slice.value / total) * circumference;
            const node = (
              <circle
                key={slice.label}
                cx="70"
                cy="70"
                r={radius}
                fill="none"
                stroke={slice.color}
                strokeWidth="14"
                strokeDasharray={`${length} ${circumference - length}`}
                strokeDashoffset={-cursor}
              />
            );
            cursor += length;
            return node;
          })}
        </g>
      </svg>
      <ul className="home__legend">
        {slices.map((slice) => (
          <li key={slice.label}>
            <i style={{ background: slice.color }} />
            <span>{slice.label}</span>
            <strong>{slice.display}</strong>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SourceBars({ rows }) {
  const max = Math.max(...rows.map((row) => row.value), 1);
  if (!rows.length) return <p className="home__empty">No credited income yet.</p>;
  return (
    <div className="home__sources">
      {rows.map((row) => (
        <div key={row.key} className="home__source">
          <span>{row.label}</span>
          <i>
            <b style={{ width: `${(row.value / max) * 100}%`, background: row.color }} />
          </i>
          <strong>{row.display}</strong>
        </div>
      ))}
    </div>
  );
}

function Chart({ series }) {
  const width = 640;
  const height = 150;
  const pad = 8;
  const max = Math.max(...series.map((point) => point.value), 1);
  const step = series.length > 1 ? (width - pad * 2) / (series.length - 1) : 0;
  const coords = series.map((point, i) => {
    const x = pad + i * step;
    const y = height - pad - (point.value / max) * (height - pad * 2);
    return { x, y };
  });
  const line = coords.map((point, i) => `${i === 0 ? 'M' : 'L'}${point.x},${point.y}`).join(' ');
  const area = `${line} L${coords[coords.length - 1]?.x || pad},${height - pad} L${pad},${height - pad} Z`;
  const ticks = [0, Math.floor((series.length - 1) / 2), series.length - 1].filter((value, i, list) => list.indexOf(value) === i);

  return (
    <>
      <svg className="home__plot" viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Earnings chart">
        <defs>
          <linearGradient id="home-earn" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#2ee6c7" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#2ee6c7" stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0.25, 0.5, 0.75].map((level) => (
          <line key={level} x1={pad} x2={width - pad} y1={pad + (height - pad * 2) * level} y2={pad + (height - pad * 2) * level} stroke="currentColor" strokeOpacity="0.12" />
        ))}
        <path d={area} fill="url(#home-earn)" />
        <path d={line} fill="none" stroke="#2ee6c7" strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" />
        {coords.map((point) => (
          <circle key={`${point.x}-${point.y}`} cx={point.x} cy={point.y} r="3.5" fill="#07111a" stroke="#2ee6c7" strokeWidth="2" />
        ))}
      </svg>
      <div className="home__plot-labels">
        {ticks.map((index) => <span key={series[index].key}>{series[index].label}</span>)}
      </div>
    </>
  );
}

export default function DashboardPage() {
  const { user, openConnect, connectOpening, config, refreshUser, setReferralUpline, loading } = useWallet();
  const toast = useToast();
  const { refWallet } = useParams();
  const [copied, setCopied] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [range, setRange] = useState('7');
  const [events, setEvents] = useState([]);
  const [cap, setCap] = useState(null);
  const [rank, setRank] = useState(null);
  const [team, setTeam] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);

  useLayoutEffect(() => {
    if (refWallet) setReferralUpline(refWallet);
  }, [refWallet, setReferralUpline]);

  useEffect(() => {
    if (loading) return undefined;
    if (!user?.wallet_address) {
      setPageLoading(false);
      setEvents([]);
      setCap(null);
      setRank(null);
      setTeam(null);
      return undefined;
    }
    let cancelled = false;
    setPageLoading(true);
    Promise.all([
      refreshUser(),
      api.myIncome().catch(() => null),
      api.myRank().catch(() => null),
      api.myTeam().catch(() => null),
    ]).then(([, incomeRes, rankRes, teamRes]) => {
      if (cancelled) return;
      setEvents(incomeRes?.events || []);
      setCap(incomeRes?.cap || null);
      setRank(rankRes?.rank || null);
      setTeam(teamRes?.summary || null);
    }).finally(() => {
      if (!cancelled) setPageLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [user?.wallet_address, loading, refreshUser]);

  const invested = Number(user?.total_invested || 0);
  const earned = Number(user?.total_earned || 0);
  const claimed = Number(user?.total_claimed || 0);
  const available = Number(user?.virtual_balance || 0);
  const multiplier = Number(cap?.multiplier || config?.earn_cap_multiplier || 2);
  const maxEarnable = Number(cap?.max_earnable ?? invested * multiplier);
  const remaining = Number(cap?.remaining ?? Math.max(0, maxEarnable - earned));
  const progress = maxEarnable > 0 ? Math.min(100, (earned / maxEarnable) * 100) : 0;
  const dfiRate = Number(config?.dfi_per_usdt || 1);
  const unclaimedDfi = available * dfiRate;
  const portfolio = invested + available;

  const inviteWallet = (user?.wallet_address || '').toLowerCase();
  const inviteLink = useMemo(() => {
    if (!inviteWallet) return '';
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    return `${origin}/dashboard/${inviteWallet}`;
  }, [inviteWallet]);

  const series = useMemo(() => buildSeries(events, Number(range) || 7), [events, range]);
  const sourceRows = useMemo(() => {
    const totals = new Map();
    events.forEach((event) => {
      const credited = Number(event.amount_credited || 0);
      if (!Number.isFinite(credited) || credited <= 0) return;
      const key = event.source_type || 'other';
      totals.set(key, (totals.get(key) || 0) + credited);
    });
    return [...totals.entries()]
      .map(([key, value]) => ({
        key,
        label: SOURCE_META[key]?.label || key,
        color: SOURCE_META[key]?.color || '#b388ff',
        value,
      }))
      .sort((a, b) => b.value - a.value);
  }, [events]);
  const rankLevel = rank?.profile?.rank ?? user?.rank_level ?? 0;

  async function copyInviteLink() {
    if (!inviteLink) return;
    try {
      await navigator.clipboard.writeText(inviteLink);
    } catch {
      const el = document.createElement('textarea');
      el.value = inviteLink;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    setCopied(true);
    toast.success('Invite link copied');
    window.setTimeout(() => setCopied(false), 1800);
  }

  async function shareInvite() {
    if (!inviteLink) return;
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Dynamic Future DeFi', text: 'Join with my invite link', url: inviteLink });
        return;
      } catch {
        return;
      }
    }
    copyInviteLink();
  }

  function show(value) {
    return hidden ? '••••' : value;
  }

  if (loading || (user && pageLoading)) return <DashboardSkeleton />;

  if (!user) {
    return (
      <section className="home__invite">
        <h2>Connect to unlock your dashboard</h2>
        <p>Connect your wallet to see your portfolio. New wallets need a refer address to register.</p>
        <div className="home__invite-actions">
          <button className={`home__action home__action--deposit${connectOpening ? ' is-busy' : ''}`} type="button" onClick={() => openConnect('Connect wallet to login or register')} disabled={connectOpening}>
            {connectOpening ? 'Connecting…' : 'Connect wallet'}
          </button>
        </div>
      </section>
    );
  }

  const payments = [
    { to: '/plans', label: 'Deposit', Icon: HiOutlineArrowDown, tone: '#2f7bff' },
    { to: '/wallet', label: 'Claim', Icon: HiOutlineArrowUp, tone: '#12b5c9' },
  ];
  const pages = [
    { to: '/income', label: 'Income', Icon: HiOutlineChartBar, tone: '#7a3cff' },
    { to: '/commission', label: 'Commission', Icon: HiOutlineChartSquareBar, tone: '#e0a106' },
    { to: '/level', label: 'Level', Icon: HiOutlineTrendingUp, tone: '#e85d8c' },
    { to: '/team', label: 'Team', Icon: HiOutlineUserGroup, tone: '#1fa971' },
    { to: '/rank', label: 'Rank', Icon: HiOutlineStar, tone: '#f07316' },
    { to: '/invite', label: 'Invite', Icon: HiOutlineUserAdd, tone: '#3d7dff' },
  ];
  const mix = [
    { label: 'Invested', value: Math.max(0, invested), color: '#f6c445', display: show(`${money(invested)} USDT`) },
    { label: 'Available', value: Math.max(0, available), color: '#3dd6ff', display: show(`${money(available)} USDT`) },
    { label: 'Claimed', value: Math.max(0, claimed), color: '#b388ff', display: show(`${money(claimed)} USDT`) },
  ];
  const sources = sourceRows.map((row) => ({ ...row, display: show(money(row.value, 4)) }));
  const liveRank = Number(rank?.live?.projected_rank ?? 0);
  const achievable = Number(rank?.live?.achievable || 0);
  const savedBusiness = Number(rank?.profile?.business || 0);
  const nextRank = (rank?.thresholds || []).find((step) => Number(step.rank) === liveRank + 1);
  const nextAmount = Number(nextRank?.amount || 0);
  const rankProgress = nextRank
    ? (nextAmount > 0 ? Math.min(100, (achievable / nextAmount) * 100) : 100)
    : (liveRank > 0 ? 100 : 0);
  const teamInvested = Number(team?.total_invested || 0);
  const teamStats = [
    { label: 'Total members', value: show(String(team?.total_members ?? 0)), Icon: HiOutlineUserGroup, tone: '#1fa971' },
    { label: 'Direct (L1)', value: show(String(team?.direct_count ?? 0)), Icon: HiOutlineUserAdd, tone: '#3d7dff' },
    { label: 'Max level', value: show(String(team?.max_level ?? 0)), Icon: HiOutlineTrendingUp, tone: '#e85d8c' },
    { label: 'Team invested', value: show(`${money(teamInvested)} USDT`), Icon: HiOutlineCash, tone: '#f6c445' },
  ];

  const stats = [
    { to: '/wallet', label: 'Unclaimed DFI', value: show(money(unclaimedDfi, 4)), Icon: RiCoinLine, tone: '#3dd6ff' },
    { to: '/plans', label: 'Total invested', value: show(`${money(invested)} USDT`), Icon: HiOutlineCash, tone: '#f6c445' },
    { to: '/income', label: 'Total earned', value: show(money(earned, 4)), Icon: HiOutlineChartBar, tone: '#7aa2ff' },
    { to: '/wallet', label: 'Total claimed', value: show(money(claimed, 4)), Icon: MdOutlineAccountBalanceWallet, tone: '#b388ff' },
    { to: '/income', label: 'Max earnable', value: show(money(maxEarnable, 4)), extra: `${multiplier}x`, Icon: HiOutlineGift, tone: '#ff6b9a' },
    { to: '/income', label: 'Cap remaining', value: show(money(remaining, 4)), Icon: HiOutlineChartBar, tone: '#2ee6c7' },
  ];

  return (
    <div className="home">
      <section className="home__hero">
        <p className="home__hero-label">
          Total Portfolio Value
          <button type="button" className="home__eye" onClick={() => setHidden((value) => !value)} aria-label={hidden ? 'Show balances' : 'Hide balances'}>
            {hidden ? <HiOutlineEyeOff /> : <HiOutlineEye />}
          </button>
        </p>
        <p className="home__value">{show(money(portfolio))} <small>USDT</small></p>
        <p className="home__fiat">≈ ${show(money(portfolio))}</p>
        <img className="home__coin" src="/logo-512.png" alt="" />
        <svg className="home__spark" viewBox="0 0 220 72" aria-hidden="true">
          <path d="M4 58 C 30 56, 40 40, 62 42 S 96 18, 118 22 S 156 8, 216 14" fill="none" stroke="rgba(255,255,255,0.85)" strokeWidth="3" strokeLinecap="round" />
        </svg>
      </section>

      <div className="home__pay">
        {payments.map((item) => (
          <Link key={item.label} className="home__action home__action--tone" to={item.to} style={{ '--tone': item.tone }}>
            <item.Icon /> {item.label}
          </Link>
        ))}
      </div>

      <div className="home__side">
        <section className="home__invite">
          <div className="home__invite-top">
            <div>
              <h2><HiOutlineUserAdd /> Invite & Earn</h2>
              <p>Share your link and earn rewards</p>
            </div>
            <Link className="home__rank" to="/rank">
              <HiOutlineStar />
              <span>
                <em>Your rank</em>
                <strong>{rankTitle(rankLevel)}</strong>
              </span>
            </Link>
          </div>
          <div className="home__link">
            <code>{inviteLink}</code>
            <button type="button" onClick={copyInviteLink} aria-label="Copy invite link"><HiOutlineDuplicate /></button>
          </div>
          <div className="home__invite-actions">
            <button className="home__action home__action--deposit" type="button" onClick={copyInviteLink}>
              <HiOutlineDuplicate /> {copied ? 'Copied' : 'Copy Link'}
            </button>
            <button className="home__action" type="button" onClick={shareInvite}>
              <HiOutlinePaperAirplane /> Share
            </button>
          </div>
        </section>
      </div>

      <div className="home__stats">
        {stats.map((item) => (
          <Link key={item.label} className="home__stat" to={item.to} style={{ '--tone': item.tone }}>
            <span className="home__stat-ico"><item.Icon /></span>
            <span className="home__stat-copy">
              <span>{item.label}</span>
              <strong>{item.value}{item.extra ? <em>{item.extra}</em> : null}</strong>
            </span>
            <HiChevronRight className="home__chev" />
          </Link>
        ))}
        <Link className="home__stat home__stat--wide" to="/income" style={{ '--tone': '#f6c445' }}>
          <span className="home__stat-ico"><HiOutlineChartBar /></span>
          <span className="home__stat-copy">
            <span>Cap progress</span>
            <strong>{show(`${progress.toFixed(1)} %`)}</strong>
            <i className="home__bar" aria-hidden="true"><i style={{ width: `${hidden ? 0 : progress}%` }} /></i>
          </span>
          <HiChevronRight className="home__chev" />
        </Link>
      </div>

      <div className="home__actions">
        {pages.map((item) => (
          <Link key={item.label} className="home__action home__action--tone" to={item.to} style={{ '--tone': item.tone }}>
            <item.Icon /> {item.label}
          </Link>
        ))}
      </div>

      <section className="home__graphs" aria-label="Graphs">
        <section className="home__chart home__chart--line">
          <div className="home__chart-top">
            <div>
              <h2>Earnings Overview</h2>
              <p>Credited income</p>
            </div>
            <select className="home__chart-select" value={range} onChange={(event) => setRange(event.target.value)} aria-label="Earnings range">
              <option value="7">7 Days</option>
              <option value="30">30 Days</option>
            </select>
          </div>
          <Chart series={series} />
        </section>
        <div className="home__team">
          {teamStats.map((item) => (
            <Link key={item.label} className="home__stat" to="/team" style={{ '--tone': item.tone }}>
              <span className="home__stat-ico"><item.Icon /></span>
              <span className="home__stat-copy">
                <span>{item.label}</span>
                <strong>{item.value}</strong>
              </span>
              <HiChevronRight className="home__chev" />
            </Link>
          ))}
        </div>
        <section className="home__chart">
          <div className="home__chart-top">
            <div>
              <h2>Portfolio mix</h2>
              <p>Invested, available, and claimed</p>
            </div>
          </div>
          <Donut slices={mix} />
        </section>
        <section className="home__chart">
          <div className="home__chart-top">
            <div>
              <h2>Income sources</h2>
              <p>Credited by type</p>
            </div>
          </div>
          <SourceBars rows={sources} />
        </section>
      </section>

      <div className="home__rank-stats">
        <Link className="home__stat" to="/rank" style={{ '--tone': '#f6c445' }}>
          <span className="home__stat-ico"><HiOutlineStar /></span>
          <span className="home__stat-copy">
            <span>Profile rank</span>
            <strong>{rankTitle(rankLevel)}</strong>
          </span>
          <HiChevronRight className="home__chev" />
        </Link>
        <Link className="home__stat" to="/rank" style={{ '--tone': '#3dd6ff' }}>
          <span className="home__stat-ico"><HiOutlineCash /></span>
          <span className="home__stat-copy">
            <span>Saved business</span>
            <strong>{show(`${money(savedBusiness)} USDT`)}</strong>
          </span>
          <HiChevronRight className="home__chev" />
        </Link>
        <Link className="home__stat" to="/rank" style={{ '--tone': '#2ee6c7' }}>
          <span className="home__stat-ico"><HiOutlineChartSquareBar /></span>
          <span className="home__stat-copy">
            <span>Live achievable</span>
            <strong>{show(`${money(achievable)} USDT`)}</strong>
          </span>
          <HiChevronRight className="home__chev" />
        </Link>
        <Link className="home__stat" to="/rank" style={{ '--tone': '#f07316' }}>
          <span className="home__stat-ico"><HiOutlineTrendingUp /></span>
          <span className="home__stat-copy">
            <span>Live rank</span>
            <strong>{rankTitle(liveRank)}</strong>
            <span className="home__load">
              <i
                className="home__load-track"
                role="progressbar"
                aria-valuenow={Math.round(rankProgress)}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label="Progress to next rank"
              >
                <i className="home__load-fill" style={{ width: `${hidden ? 0 : rankProgress}%` }} />
                <i className="home__load-shine" />
              </i>
              <em>{show(`${rankProgress.toFixed(1)}%`)}</em>
            </span>
          </span>
          <HiChevronRight className="home__chev" />
        </Link>
      </div>

      <TodayDepositBoard registeredAt={user?.created_at} />
      <PancakeSwapGuide config={config} variant="home" />
    </div>
  );
}
